import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, linkedSignal, output } from '@angular/core';
import { PersonLogin } from '../person/person-login';
import { SelectionItem } from '../selections/selection-item';
import { OrganizationAddressService } from '../services/organization-address-service';
import { SelectionService } from '../services/selection-service';
import { StringUtils } from '../utils/string-utils';
import { OrganizationAddress } from './organization-address';
import { AddressUtils } from '../utils/address-utils';
import { ValidatedAddressInfo } from '../model/validated-address-info';
import { MatDialog } from '@angular/material/dialog';
import { AddressAlternativeDialogComponent } from '../dialogs/address-alternative-dialog/address-alternative-dialog.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'org-address-detail',
  templateUrl: './org-address-detail.component.html',
  styleUrls: ['./org-address-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButton, MatIcon, NgClass, MatProgressSpinner, DatePipe]
})
export class OrgAddressDetailComponent implements OnInit {
  private readonly addressService = inject(OrganizationAddressService);
  private readonly selectionService = inject(SelectionService);
  private readonly dialog = inject(MatDialog);


  public readonly addressInput = input.required<OrganizationAddress>({ alias: 'address' });
  public readonly workingAddress = linkedSignal(() => this.addressInput());
  public readonly personLogin = input<PersonLogin>(undefined!);
  public readonly isEdit = input.required<boolean>();
  public readonly isDealerAddress = input<boolean>(false);
  public readonly addressSaved = output<OrganizationAddress>();

  public readonly errorMessage = signal<string>(undefined!);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly stateCodes = signal<SelectionItem[]>(undefined!);
  public readonly countryCodes = signal<SelectionItem[]>(undefined!);
  public readonly addressTypes = signal<SelectionItem[]>(undefined!);

  public save(): void {
    this.errorMessage.set(null!);
    this.isBusy.set(true);
    if (AddressUtils.isGeneralDeliverySpecified(this.workingAddress())) {
      this.saveAddress();
    } else {
      this.validateAndSaveAddress();
    }
  }

  private validateAndSaveAddress(): void {
    this.addressService.validateAddress(this.workingAddress()).subscribe(resolution => {
      if (!resolution.validAddress || AddressUtils.validatedAddressIsDifferent(resolution.validatedAddresses, this.workingAddress())) {
        const validatedAddress: ValidatedAddressInfo = resolution.validAddress
          ? resolution.validatedAddresses[0]!
          : AddressUtils.injectGeneralDeliveryLine(this.workingAddress())!;
        const suggestionDialog = this.dialog.open(AddressAlternativeDialogComponent, {
          width: '1024px',
          height: '480px',
          data: { resolution: resolution, enteredAddress: this.workingAddress(), validatedAddress: validatedAddress },
        });
        suggestionDialog.afterClosed().subscribe(result => {
          if (result) {
            this.workingAddress.set(AddressUtils.updateAddressWithValidated(this.workingAddress(), validatedAddress));
            this.saveAddress();
          } else {
            if (resolution.validAddress) {
              this.saveAddress();
            } else {
              this.isBusy.set(false);
            }
          }
        }, error => this.handleError(error));
      } else {
        this.saveAddress();
      }
    }, error => this.handleError(error));
  }

  private saveAddress(): void {
    this.workingAddress().updateUser = this.personLogin().person.userLogin;
    this.addressService.save(this.workingAddress()).subscribe(result => {
      if (result) {
        this.workingAddress.set(result);
      }
      this.addressSaved.emit(this.workingAddress());
      this.isBusy.set(false);
    }, error => this.handleError(error));
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public cancel(): void {
    this.addressSaved.emit(null!);
  }

  public setDefaultBillingAddress(): void {
    this.addressService.setDefaultBillingAddress(this.workingAddress().orgUnitId, this.workingAddress().addressId)
      .subscribe(() => this.addressSaved.emit(this.workingAddress()));
  }

  public setDefaultShippingAddress(): void {
    this.addressService.setDefaultShippingAddress(this.workingAddress().orgUnitId, this.workingAddress().addressId)
      .subscribe(() => this.addressSaved.emit(this.workingAddress()));
  }

  ngOnInit(): void {
    this.selectionService.getStateCodes().subscribe(result => this.stateCodes.set(result));
    this.selectionService.getCountryCodes().subscribe(result => {
      this.countryCodes.set(result);
      if (!this.workingAddress()?.countryCode) {
        this.workingAddress().countryCode = result.find(c => c.description === 'United States')?.code!;
      }
    });
    this.selectionService.getAddressTypes().subscribe(result => this.addressTypes.set(result));
  }

}
